import { useCallback, useMemo } from 'react';

import { showConfirm } from '@/components/ui/alert';
import { avatarDefault } from '@/assets/images';
import { STRINGS } from '@/constants/strings';
import { useGuardianProfileQuery } from '@/features/guardian/queries';
import { googleSignOut } from '@/libs/firebase/auth';
import { useSessionStore } from '@/stores';

import { getGuardianName } from '../utils';
import type { CoGuardian } from '../types';

export function useGuardianProfile() {
  const user = useSessionStore(state => state.user);
  const query = useGuardianProfileQuery(user?.uid);

  const identity = useMemo(
    () => ({
      name: getGuardianName(query.data, user?.displayName),
      avatarUri: query.data?.photoUrl || user?.photoURL || undefined,
    }),
    [query.data, user?.displayName, user?.photoURL],
  );

  const coGuardians = useMemo<CoGuardian[]>(
    () =>
      (query.data?.coGuardians ?? []).map((guardian, index) => ({
        id: `${guardian.firstname ?? ''}-${guardian.lastname ?? ''}-${index}`,
        name:
          [guardian.firstname, guardian.lastname].filter(Boolean).join(' ').trim() ||
          STRINGS.guardianProfile.fallbackName,
        relationshipSummary: STRINGS.guardianProfile.coGuardians.mutual(guardian.mutual),
        avatar: guardian.photoUrl ? { uri: guardian.photoUrl } : avatarDefault,
      })),
    [query.data?.coGuardians],
  );

  const requestLogout = useCallback(async () => {
    const confirmed = await showConfirm({
      title: STRINGS.guardianProfile.logout.alertTitle,
      message: STRINGS.guardianProfile.logout.alertMessage,
      confirmText: STRINGS.guardianProfile.logout.confirm,
      cancelText: STRINGS.guardianProfile.logout.cancel,
      confirmStyle: 'destructive',
      confirmFirst: true,
    });
    if (!confirmed) return;

    googleSignOut();
    useSessionStore.getState().setSoftLoggedOut(true);
  }, []);

  return { ...query, ...identity, coGuardians, requestLogout };
}
